import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { supabase } from '../config/supabase'
import Footer from '../components/Footer'
import { Helmet } from 'react-helmet-async'

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [payment, setPayment] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const paymentId = searchParams.get('id')
    if (!paymentId) {
      navigate('/dashboard')
      return
    }
    getPaymentDetails(paymentId)
  }, [searchParams])

  const getPaymentDetails = async (paymentId) => {
    try {
      const { data, error } = await supabase
        .from('payments')
        .select(`
          *,
          package:packages(*)
        `)
        .eq('id', paymentId)
        .single()

      if (error) throw error
      setPayment(data)
    } catch (error) {
      console.error('Error fetching payment:', error)
      navigate('/dashboard')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) return null

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>แจ้งชำระเงินสำเร็จ</title>
      </Helmet>
      <main className="flex-grow">
        <div className="max-w-2xl mx-auto p-6">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-4xl font-bold mb-4">แจ้งชำระเงินสำเร็จ</h1>
            <p className="text-gray-400">
              ขอบคุณที่ไว้วางใจเรา ทีมงานจะรีบตรวจสอบและดำเนินการให้เร็วที่สุด
            </p>
          </div>

          <div className="bg-gray-900 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">รายละเอียดกรชำระเงิน</h2>
            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-gray-800">
                <span className="text-gray-400">หมายเลขคำสั่งซื้อ</span>
                <span>{payment.id}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-800">
                <span className="text-gray-400">แพ็กเกจ</span>
                <span className="font-medium">
                  {payment.package?.name}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-800">
                <span className="text-gray-400">จำนวนเงิน</span>
                <span className="text-xl font-bold text-yellow-400">{payment.amount}฿</span>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-800">
                <span className="text-gray-400">วันเวลาที่โอน</span>
                <span>{payment.transfer_date} {payment.transfer_time}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-800">
                <span className="text-gray-400">ธนาคารที่โอน</span>
                <span>{payment.bank_name}</span>
              </div>
              <div className="flex justify-between py-3">
                <span className="text-gray-400">วันที่แจ้งชำระเงิน</span>
                <span>{formatDate(payment.created_at)}</span>
              </div>
            </div>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-yellow-500/20 rounded-lg">
                <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-yellow-400 mb-2">รอตรวจสอบ</h3>
                <p className="text-gray-400 leading-relaxed">
                  ทีมงานจะตรวจสอบการชำระเงินของคุณภายใน 24 ชั่วโมง 
                  เมื่อตรวจสอบเรียบร้อยแล้ว ระบบจะอัพเดทสถานะสมาชิก VIP ในเว็บไซต์ให้อัตโนมัติ
                </p>
              </div>
            </div>
          </div>
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-green-500/20 rounded-lg">
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-green-400 mb-2">ขั้นตอนถัดไป</h3>
                <p className="text-gray-300 text-lg mb-6">
                  แจ้งอีเมลในห้องดิสคอร์ดเพื่อรับสถานะ VIP สำหรับการเข้าถึงเนื้อหาต่างๆ เช่นโปรแกรม MetaMind และ Custom GPT ต่างๆ โดยการกดปุ่มเข้า discord ด้านล่าง และทำตามขั้นตอน
                </p>

                <div className="w-full bg-white rounded-lg shadow-lg p-6 mb-6">
                  <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                    <iframe
                      src="https://www.youtube.com/embed/HgMOVII2ixI"
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    />
                  </div>
                </div>

                {/* แสดงรูปภาพสเต็ป */}
                <div className="space-y-4 mt-4">
                  <div className="bg-gray-800 rounded-lg p-4">
                    <img 
                      src="/images/next1.png" 
                      alt="Step 1" 
                      className="w-full h-auto rounded-lg shadow-lg"
                    />
                    <p className="text-center mt-2 text-gray-400">สเต็ปที่ 1</p>
                  </div>

                  <div className="bg-gray-800 rounded-lg p-4">
                    <img 
                      src="/images/next2.png" 
                      alt="Step 2" 
                      className="w-full h-auto rounded-lg shadow-lg"
                    />
                    <p className="text-center mt-2 text-gray-400">สเต็ปที่ 2</p>
                  </div>

                  <div className="bg-gray-800 rounded-lg p-4">
                    <img 
                      src="/images/next3.png" 
                      alt="Step 3" 
                      className="w-full h-auto rounded-lg shadow-lg"
                    />
                    <p className="text-center mt-2 text-gray-400">สเต็ปที่ 3</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button 
              onClick={() => navigate('/dashboard')}
              className="bg-gray-800 hover:bg-gray-700 px-6 py-3 rounded-lg transition-colors mx-2"
            >
              กลับสู่หน้าหลัก
            </button>
            <div className="flex justify-center gap-4 mt-8">
              <a
                href="https://discord.com/invite/MxgKZkydXJ"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 rounded-lg text-white font-medium transition-colors"
              >
                <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
                เข้า Discord
              </a>
              <a
                href="https://www.facebook.com/aiunlockedvip"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-colors"
              >
                <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default PaymentSuccess